import { useCallback, useContext } from "react";
import Swal from "sweetalert2";

import { QueryParamsContext } from "../../../../context/QueryParamsContext";
import {
  createContratoServico,
  findContratoServico,
  getViewLists,
  updateContratoServico,
} from "../../../../repositories/qerpapi/erp/faturamento/ClassificacaoServicoRepository";
import {
  getClassificacaoTributaria,
  getIndicadorOperacao,
  getCodigoTributacao,
  getNbs,
  getNbsDados,
  getPerfilTributario,
} from "../../../../repositories/qerpapi/impostos/ImpostosRepository";
import { indexTypeContractByCompany } from "../../../../repositories/qerpapi/erp/faturamento/TipoContratosRepository";
import MaskService from "../../../../utils/mask/maskService";
import { notify } from "../../../../utils/notify";

import { validNotNull } from "../../../../utils/validations";
import { ClassificacaoServicoContext } from "../contexts/ClassificacaoServicoContext";
import { useHistory } from "react-router-dom";
import { ConfigurationContext } from "../../gerenciamentocontrato/contexts/ConfigurationContext";
import { findConfigurationV3 } from "../../../../repositories/qerpapi/v3/ConfigurationRepository";

const useClassificacaoServicoController = () => {
  const {
    active,
    setActive,
    setLoading,
    companies,
    setCompanies,
    setCostCenters,
    tableSetCostCenters,
    setTableSetCostCenters,
    description,
    value,
    setErrors,
    percentage,
    setActiveStep,
    selectedCostCenter,
    setSelectedCostCenter,
    setPercentage,
    classId,
    setClassId,
    setDescription,
    listServiceType,
    setServiceType,
    serviceType,
    setValue,
    ratioLimitPercentage,
    setRatioLimitPercentage,
    servicos,
    setServicos,
    nbs,
    setNbs,
    indicadorOperacao,
    setIndicadorOperacao,
    classificacaoTributaria,
    setClassificacaoTributaria,
    codigoTributacao,
    setCodigoTributacao,
    perfilTributario,
    setPerfilTributario,
    servicosOptions,
    nbsOptions,
    indicadorOptions,
    classificacaoOptions,
    codTributacaoOptions,
    perfilTributarioOptions,
    setPerfilTributarioOptions,
    setClassificacaoOptions,
    setIndicadorOptions,
  } = useContext(ClassificacaoServicoContext);
  const { queryParams } = useContext(QueryParamsContext);
  const { push } = useHistory();
  const { configuration, setConfiguration } = useContext(ConfigurationContext);

  const rmdPercent = (value) => MaskService.removeMaskDecimal(value, 2);

  const rmdMoney = (
    value,
    decimals = configuration?.contract_decimal_size ?? 2
  ) => MaskService.removeMaskDecimal(value, decimals);

  const maskPercent = (value) => {
    return MaskService.toMask(
      "decimal",
      parseFloat(value || 0.0).toFixed(2),
      2
    );
  };

  const ensureConfiguration = useCallback(async () => {
    if (configuration) return configuration;
    const configurationCall = await findConfigurationV3();
    if (configurationCall?.success) {
      setConfiguration(configurationCall.data);
      return configurationCall.data;
    }
    return null;
  }, [configuration, setConfiguration]);

  const handleSetData = useCallback(
    async (id = null) => {
      if (!queryParams?.empresa_cnpj || !queryParams?.empresa_id) return;

      try {
        setLoading(true);

        const configResolved = await ensureConfiguration();
        const contractDecimals = configResolved?.contract_decimal_size ?? 2;
        const maskMoney = (value) =>
          MaskService.toMask(
            "decimal",
            parseFloat(value || 0.0).toFixed(contractDecimals),
            contractDecimals
          );

        const callbackLists = await getViewLists({
          empresa_cnpj: queryParams.empresa_cnpj,
          empresa_id: queryParams.empresa_id,
        });

        if (!callbackLists?.success) return notify("Falha na Requisição");

        const requestCompanies = callbackLists.data?.empresas[0] || [];

        const planonegocios = [];
        (callbackLists.data?.centrocustos || []).forEach((item) => {
          item["filhos"] = item.planonegocios;
          planonegocios.push(item);
        });
        setCostCenters(planonegocios);

        if (id) {
          const callback = await findContratoServico({
            empresa_cnpj: queryParams.empresa_cnpj,
            id: id,
            include:
              "contratoservicoempresa,contratoservicocentrocusto.centrocustos",
          });

          if (!callback?.success)
            return notify("Erro ao buscar dados do serviço");

          const classif = callback.data;
          const tableSetCostCenters = [];
          let maxPercent = 100.0;

          (classif?.contratoservicocentrocusto || []).forEach((item, index) => {
            tableSetCostCenters.push(
              createCostCenterItem({
                index: index,
                costDescription: item?.centrocustos[0]?.descricao,
                costCenterId: item.idcentrocustos,
                costPercentage: maskPercent(item.porcentagem),
                businessCenterId: item.idcentronegocios,
              })
            );

            maxPercent -= rmdPercent(maskPercent(item.porcentagem));
          });

          const [
            servicosData,
            classificacaoData,
            indicadorData,
            codTributacaoData,
            nbsData,
            perfilTributarioData,
          ] = await Promise.all([
            servicosOptions?.length
              ? Promise.resolve({ data: servicosOptions })
              : indexTypeContractByCompany({
                  empresa_cnpj: queryParams.empresa_cnpj,
                  empresa_id: queryParams.empresa_id,
                  where: { ativo: "S" },
                }),
            classificacaoOptions?.length
              ? Promise.resolve(classificacaoOptions)
              : getClassificacaoTributaria(),
            indicadorOptions?.length
              ? Promise.resolve(indicadorOptions)
              : getIndicadorOperacao(),
            codTributacaoOptions?.length
              ? Promise.resolve(codTributacaoOptions)
              : getCodigoTributacao(),
            nbsOptions?.length ? Promise.resolve(nbsOptions) : getNbs(),
            perfilTributarioOptions?.length
              ? Promise.resolve(perfilTributarioOptions)
              : getPerfilTributario(),
          ]);

          const servicosList = servicosData?.data || servicosData || [];
          const nbsList = nbsData || [];
          const codTributacaoList = codTributacaoData || [];
          const allClassificacaoOptions = classificacaoData || [];
          const allIndicadorOptions = indicadorData || [];
          const perfilList = perfilTributarioData || [];

          const servicoCompleto = classif.tipocontratos_id
            ? servicosList.find(
                (item) => item.id === classif.tipocontratos_id
              ) || null
            : null;

          const nbsCompleto = classif.nbs_codigo
            ? nbsList.find((item) => item.codigo === classif.nbs_codigo) || null
            : null;

          let indicadorCompleto = classif.indicador_operacao_codigo
            ? allIndicadorOptions.find(
                (item) => item.codigo === classif.indicador_operacao_codigo
              ) || null
            : null;

          let classificacaoCompleta = classif.classificacao_tributaria_codigo
            ? allClassificacaoOptions.find(
                (item) =>
                  item.codigo === classif.classificacao_tributaria_codigo
              ) || null
            : null;

          const codigoTributacaoCompleto = classif.tributacao_nacional_codigo
            ? codTributacaoList.find(
                (item) => item.codigo === classif.tributacao_nacional_codigo
              ) || null
            : null;

          const perfilAtual =
            perfilList.find((p) => p.id === classif.perfil_tributario_id) ||
            null;
          const opçõesDisponíveis = perfilList.filter((p) => p.ativo && p.nfse);

          const perfilAtualEstaInvisivel =
            perfilAtual &&
            !opçõesDisponíveis.some((p) => p.id === perfilAtual.id);

          if (perfilAtualEstaInvisivel) {
            opçõesDisponíveis.push(perfilAtual);
          }

          setPerfilTributarioOptions(opçõesDisponíveis);

          if (nbsCompleto?.codigo) {
            try {
              const nbsData = await getNbsDados(nbsCompleto.codigo);

              const indicadorIds = new Set();
              (nbsData || []).forEach((item) => {
                if (item?.indicadorTributacaoConsumo?.codigo) {
                  indicadorIds.add(item.indicadorTributacaoConsumo.codigo);
                }
              });

              if (indicadorIds.size > 0) {
                const filteredIndicador = allIndicadorOptions.filter((option) =>
                  indicadorIds.has(option.codigo)
                );

                if (
                  indicadorCompleto &&
                  !filteredIndicador.find(
                    (opt) => opt.codigo === indicadorCompleto.codigo
                  )
                ) {
                  filteredIndicador.push(indicadorCompleto);
                }

                setIndicadorOptions(filteredIndicador);
              } else {
                setIndicadorOptions(allIndicadorOptions);
              }

              const validClassificacaoIds = new Set();
              (nbsData || []).forEach((item) => {
                const hasClassificacao = item?.classificacaoTributaria?.codigo;
                const matchesIndicador =
                  !indicadorCompleto ||
                  item?.indicadorTributacaoConsumo?.codigo ===
                    indicadorCompleto.codigo;

                if (hasClassificacao && matchesIndicador) {
                  validClassificacaoIds.add(
                    item.classificacaoTributaria.codigo
                  );
                }
              });

              if (validClassificacaoIds.size > 0) {
                const filteredClassificacao = allClassificacaoOptions.filter(
                  (opt) => validClassificacaoIds.has(opt.codigo)
                );

                if (
                  classificacaoCompleta &&
                  !filteredClassificacao.find(
                    (opt) => opt.codigo === classificacaoCompleta.codigo
                  )
                ) {
                  filteredClassificacao.push(classificacaoCompleta);
                }

                setClassificacaoOptions(filteredClassificacao);
              } else {
                if (classificacaoCompleta) {
                  setClassificacaoOptions([classificacaoCompleta]);
                } else {
                  setClassificacaoOptions(
                    indicadorCompleto ? [] : allClassificacaoOptions
                  );
                }
              }
            } catch (error) {
              console.error("Erro ao buscar dados do NBS:", error);
              setIndicadorOptions(allIndicadorOptions);
              setClassificacaoOptions(allClassificacaoOptions);
            }
          } else {
            setIndicadorOptions(allIndicadorOptions);
            setClassificacaoOptions(allClassificacaoOptions);
          }

          setClassId(classif.id);
          setActive(classif.ativo === "S");
          setDescription(classif.descricao);
          setServicos(servicoCompleto);
          setNbs(nbsCompleto);
          setIndicadorOperacao(indicadorCompleto);
          setClassificacaoTributaria(classificacaoCompleta);
          setCodigoTributacao(codigoTributacaoCompleto);
          setPerfilTributario(perfilAtual);
          setServiceType(
            listServiceType.find((type) => type.value === classif.tiposervico)
          );
          setValue(maskMoney(classif.valor));
          setTableSetCostCenters(tableSetCostCenters);
          setCompanies(
            requestCompanies.map((e) => ({
              ...e,
              check: (classif?.contratoservicoempresa || []).some(
                (comp) => comp.idempresa.toString() === e.id.toString()
              ),
            }))
          );
          setRatioLimitPercentage(maskPercent(maxPercent) || "100,00");
        } else {
          setCompanies(requestCompanies.map((e) => ({ ...e, check: true })));
          setPerfilTributario(null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      queryParams?.empresa_cnpj,
      queryParams?.empresa_id,
      setLoading,
      setClassId,
      setActive,
      setDescription,
      setServiceType,
      setValue,
      setTableSetCostCenters,
      listServiceType,
      setCostCenters,
      setCompanies,
      setRatioLimitPercentage,
      setServicos,
      setNbs,
      setIndicadorOperacao,
      setClassificacaoTributaria,
      setCodigoTributacao,
      setPerfilTributario,
      setPerfilTributarioOptions,
      setClassificacaoOptions,
      setIndicadorOptions,
      ensureConfiguration,
    ]
  );

  const handleSubmit = async (newRegister = false, utilizaNovaNfse = false) => {
    const configData = await ensureConfiguration();
    const moneyDecimals = configData?.contract_decimal_size ?? 2;
    const toMoney = (v) => MaskService.removeMaskDecimal(v, moneyDecimals);

    const jsonValidMain = [
      { key: "description", value: description },
      {
        key: "value",
        value: toMoney(value),
      },
    ];

    if (utilizaNovaNfse) {
      jsonValidMain.push({
        key: "perfilTributario",
        value: perfilTributario?.id ?? null,
      });
    }

    const validMain = validNotNull(jsonValidMain);
    setErrors(validMain.errorsValidation);

    if (validMain.error) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return notify(
        "Verifique se os campos foram preenchidos corretamente.",
        true,
        "warning"
      );
    }

    const checks = companies.find((company) => company.check);
    if (!checks)
      return notify("Selecione ao menos uma empresa", true, "warning");

    const contratoservicoempresa = [];
    companies.forEach((company) => {
      if (company.check) {
        contratoservicoempresa.push({
          idempresa: company.id,
        });
      }
    });

    const contratoservicocentrocusto = [];
    tableSetCostCenters.forEach((item) => {
      contratoservicocentrocusto.push({
        idcentrocustos: item.costCenterId,
        idcentronegocios: item.businessCenterId,
        porcentagem: rmdPercent(item.costPercentage),
      });
    });

    const body = {
      contratoservicos: {
        descricao: description,
        valor: toMoney(value),
        ativo: active ? "S" : "N",
        tiposervico: serviceType?.value,
        tipocontratos_id: servicos?.id || null,
        nbs_codigo: nbs?.codigo || null,
        indicador_operacao_codigo: indicadorOperacao?.codigo || null,
        classificacao_tributaria_codigo:
          classificacaoTributaria?.codigo || null,
        tributacao_nacional_codigo: codigoTributacao?.codigo || null,
        perfil_tributario_id: perfilTributario?.id ?? null,
        contratoservicoempresa: contratoservicoempresa,
        contratoservicocentrocusto: contratoservicocentrocusto,
      },
    };

    let callback = {};
    try {
      setLoading(true);
      if (classId) {
        callback = await updateContratoServico({
          id: classId,
          empresa_cnpj: queryParams.empresa_cnpj,
          body: body,
        });
      } else {
        callback = await createContratoServico({
          empresa_cnpj: queryParams.empresa_cnpj,
          body: body,
        });
      }

      if (!callback?.success)
        return notify("Erro ao cadastrar classificação de serviço");

      if (newRegister) {
        clear();
      } else {
        clear();
        push("listagem");
      }

      return notify(
        `Classificação de Serviço ${classId ? `Atualizada` : `Cadastrada`}`,
        true,
        "success"
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setErrors({});
    setClassId(null);
    setCodigoTributacao(null);
    setPerfilTributario(null);
    setClassificacaoTributaria(null);
    setIndicadorOperacao(null);
    setNbs(null);
    setServicos(null);
    setActive(true);
    setDescription("");
    const decimalSize = configuration?.contract_decimal_size ?? 2;
    setValue(`0,${"0".repeat(decimalSize)}`);
    setServiceType({
      label: "Avulso",
      value: "A",
    });
    setSelectedCostCenter(null);
    setTableSetCostCenters([]);
    setPercentage("0,00");
    setActiveStep(0);
    setCompanies((current) => current.map((e) => ({ ...e, check: true })));
    setRatioLimitPercentage("100,00");
  };

  const selectCenterCostAction = (branche) => {
    setSelectedCostCenter(branche);
    setActiveStep(1);
  };

  const confirmCenterCost = (setOpen = () => {}) => {
    if (!selectedCostCenter) return;

    if (!rmdPercent(percentage))
      return notify("Insira uma porcentagem valida.", true, "warning");

    const copyTable = [...tableSetCostCenters];

    copyTable.push(
      createCostCenterItem({
        index: copyTable.length,
        costDescription: selectedCostCenter.descricao,
        costCenterId: selectedCostCenter.id,
        costPercentage: percentage,
        businessCenterId: selectedCostCenter.idcentronegocio,
      })
    );

    setActiveStep(0);
    setPercentage("100,00");
    setTableSetCostCenters(copyTable);
    setSelectedCostCenter(null);
    setOpen(false);
    calcMaxPercentage();
  };

  const createCostCenterItem = ({
    index,
    costDescription,
    costCenterId,
    costPercentage,
    businessCenterId,
  }) => {
    return {
      index: index,
      costDescription: costDescription,
      costCenterId: costCenterId,
      costPercentage: costPercentage,
      businessCenterId: businessCenterId,
    };
  };

  const confirmDeleteItem = (index, listTable, setListTable) => {
    Swal.fire({
      icon: "question",
      text: "Tem certeza que deseja remover o item?",
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonText: "Não",
      confirmButtonText: "Sim",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const copy = [...listTable];
        copy.splice(index, 1);

        //Reordenação dos Indexs
        copy.forEach((value, newIndex) => {
          value.index = newIndex;
        });
        setListTable(copy);
      }
    });
  };

  const handleCheckCompany = (companyId) => {
    const copyCheckCompanies = [...companies];
    const companyIndex = copyCheckCompanies.findIndex(
      (comp) => comp.id === companyId
    );

    copyCheckCompanies[companyIndex] = {
      ...copyCheckCompanies[companyIndex],
      check: !copyCheckCompanies[companyIndex].check,
    };

    if (!copyCheckCompanies.filter((e) => e.check).length)
      return notify("Ao menos uma empresa deve estar selecionada.");

    setCompanies(copyCheckCompanies);
  };

  const handleOpenBusinessCenter = (setOpen = () => {}) => {
    let percentage = calcMaxPercentage();
    percentage = rmdPercent(percentage);

    if (percentage <= 0)
      return notify("Soma do Rateio maior de 100 (%) ", true, "warning");

    setOpen(true);
  };

  const calcMaxPercentage = useCallback(
    (currentPercentage = "0,0") => {
      const copyTable = [...tableSetCostCenters];

      let newPercentageLimit = 100.0 + rmdPercent(currentPercentage);

      copyTable.forEach((item) => {
        newPercentageLimit -= rmdPercent(item.costPercentage);
      });

      setRatioLimitPercentage(maskPercent(newPercentageLimit));
      return maskPercent(newPercentageLimit);
    },
    [setRatioLimitPercentage, tableSetCostCenters]
  );

  const handleMaxPercentage = useCallback(
    (value, setNewPercentage = () => {}) => {
      const newPercentage = rmdPercent(value);
      const percentageLimit = rmdPercent(ratioLimitPercentage);

      if (newPercentage > percentageLimit) {
        setNewPercentage(maskPercent(percentageLimit));
        return;
      }

      setNewPercentage(maskPercent(newPercentage));
    },
    [ratioLimitPercentage]
  );

  return {
    selectCenterCostAction,
    confirmDeleteItem,
    handleCheckCompany,
    handleSubmit,
    confirmCenterCost,
    handleSetData,
    clear,
    handleOpenBusinessCenter,
    handleMaxPercentage,
    calcMaxPercentage,
    mask: maskPercent,
    rmd: rmdPercent,
    rmdMoney,
  };
};

export default useClassificacaoServicoController;
